/**
 * Radical PDP: gallery zoom/pan, thumbnail swaps, smart back link, main image sync on variant:update.
 * Variants and cart use Horizon variant-picker + product-form (same as default product template).
 */
(function () {
  const PDP_SEL = '[data-radical-pdp]';
  const VARIANT_UPDATE = 'variant:update';
  const QUANTITY_SELECTOR_UPDATE = 'quantity-selector:update';
  /** RDP shop listing route segments (supports legacy page routes and new collection routes). */
  const RDP_SHOP_REF_SEGMENTS = new Set([
    'shop-rdp-single-transfers',
    'shop-rdp-gang-sheets',
    'rdp-single-transfers',
    'rdp-gang-sheets',
  ]);

  /**
   * @param {string} referrer
   * @param {string} currentHref
   */
  function isAllowedRdpShopReferrer(referrer, currentHref) {
    if (!referrer) return false;
    try {
      const ref = new URL(referrer);
      const cur = new URL(currentHref);
      if (ref.origin !== cur.origin) return false;
      const parts = ref.pathname.split('/').filter(Boolean);
      for (let i = 0; i < parts.length; i++) {
        if (RDP_SHOP_REF_SEGMENTS.has(parts[i])) return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  /**
   * @param {HTMLElement} root
   */
  function applySmartBackLink(root) {
    const link = root.querySelector('[data-radical-pdp-back]');
    if (!link || !document.referrer) return;
    if (!isAllowedRdpShopReferrer(document.referrer, window.location.href)) return;

    link.setAttribute('href', document.referrer);

    link.addEventListener(
      'click',
      (e) => {
        if (history.length > 1) {
          e.preventDefault();
          history.back();
        }
      },
      { passive: false }
    );
  }

  /**
   * Fresh Press guide tabs: progressive enhancement.
   * Without JS, all panels remain visible in source order.
   *
   * @param {HTMLElement} root
   */
  function initFreshPressTabs(root) {
    const guide = root.querySelector('[data-fresh-guide]');
    const tablist = root.querySelector('[data-fresh-guide-tabs]');
    if (!guide || !tablist) return;

    const tabs = Array.from(tablist.querySelectorAll('[data-fresh-tab]'));
    const panels = Array.from(guide.querySelectorAll('[data-fresh-panel]'));
    if (!tabs.length || !panels.length) return;

    function setActive(nextKey, moveFocus) {
      let activeKey = nextKey;
      if (!panels.some((p) => p.getAttribute('data-fresh-panel') === activeKey)) {
        activeKey = tabs[0].getAttribute('data-fresh-tab');
      }

      tabs.forEach((tab) => {
        const isActive = tab.getAttribute('data-fresh-tab') === activeKey;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
        if (isActive && moveFocus) tab.focus();
      });

      panels.forEach((panel) => {
        const isActive = panel.getAttribute('data-fresh-panel') === activeKey;
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;
      });
    }

    tabs.forEach((tab, idx) => {
      tab.addEventListener('click', () => {
        const key = tab.getAttribute('data-fresh-tab');
        if (!key) return;
        setActive(key, false);
      });

      tab.addEventListener('keydown', (e) => {
        let nextIdx = idx;
        if (e.key === 'ArrowRight') nextIdx = (idx + 1) % tabs.length;
        else if (e.key === 'ArrowLeft') nextIdx = (idx - 1 + tabs.length) % tabs.length;
        else if (e.key === 'Home') nextIdx = 0;
        else if (e.key === 'End') nextIdx = tabs.length - 1;
        else return;

        e.preventDefault();
        const nextKey = tabs[nextIdx]?.getAttribute('data-fresh-tab');
        if (!nextKey) return;
        setActive(nextKey, true);
      });
    });

    const initialActive =
      tabs.find((tab) => tab.getAttribute('aria-selected') === 'true')?.getAttribute('data-fresh-tab') ||
      tabs[0].getAttribute('data-fresh-tab');

    guide.classList.add('is-tabs-ready');
    setActive(initialActive, false);
  }

  /**
   * @param {HTMLElement} root
   * @param {HTMLImageElement | null} mainImg
   */
  function initZoom(root, mainImg) {
    const stage = root.querySelector('[data-radical-pdp-stage]');
    const layer = root.querySelector('[data-radical-pdp-zoom-layer]');
    const inner = root.querySelector('[data-radical-pdp-zoom-inner]');
    const toggle = root.querySelector('[data-radical-pdp-zoom-toggle]');
    const hint = root.querySelector('[data-radical-pdp-zoom-hint]');
    const iconIn = toggle?.querySelector('.radical-pdp__zoom-icon--in');
    const iconOut = toggle?.querySelector('.radical-pdp__zoom-icon--out');
    if (!stage || !layer || !inner || !mainImg) return;

    let isZoomed = false;
    let panX = 0;
    let panY = 0;

    let suppressNextInnerClick = false;
    let touchPanActive = false;
    let lastTouchX = 0;
    let lastTouchY = 0;
    let touchDidMove = false;

    function clampPan() {
      const rect = stage.getBoundingClientRect();
      if (rect.width < 1 || rect.height < 1) return;
      const maxPanX = rect.width * 0.42;
      const maxPanY = rect.height * 0.42;
      panX = Math.min(maxPanX, Math.max(-maxPanX, panX));
      panY = Math.min(maxPanY, Math.max(-maxPanY, panY));
    }

    function applyTransform() {
      const scale = isZoomed ? 2 : 1;
      mainImg.style.transform = `scale(${scale}) translate(${panX}px, ${panY}px)`;
      mainImg.style.cursor = isZoomed ? 'crosshair' : 'zoom-in';
    }

    function updateChrome() {
      stage.classList.toggle('radical-pdp__stage--zoomed', isZoomed);
      hint?.classList.toggle('hidden', !isZoomed);
      iconIn?.classList.toggle('hidden', isZoomed);
      iconOut?.classList.toggle('hidden', !isZoomed);
      if (toggle) {
        const zin = toggle.getAttribute('data-aria-zoom-in') || 'Zoom in';
        const zout = toggle.getAttribute('data-aria-zoom-out') || 'Zoom out';
        toggle.setAttribute('aria-label', isZoomed ? zout : zin);
      }
      applyTransform();
    }

    function resetZoom() {
      isZoomed = false;
      panX = 0;
      panY = 0;
      touchPanActive = false;
      mainImg.style.transition = 'transform 0.2s ease';
      updateChrome();
    }

    function setPanFromClient(clientX, clientY) {
      const rect = stage.getBoundingClientRect();
      if (rect.width < 1 || rect.height < 1) return;
      const nx = (clientX - rect.left) / rect.width;
      const ny = (clientY - rect.top) / rect.height;
      const cx = Math.min(1, Math.max(0, nx));
      const cy = Math.min(1, Math.max(0, ny));
      const maxPanX = rect.width * 0.42;
      const maxPanY = rect.height * 0.42;
      panX = (0.5 - cx) * 2 * maxPanX;
      panY = (0.5 - cy) * 2 * maxPanY;
      clampPan();
      mainImg.style.transition = 'none';
      applyTransform();
    }

    function toggleZoom() {
      isZoomed = !isZoomed;
      panX = 0;
      panY = 0;
      touchPanActive = false;
      mainImg.style.transition = 'transform 0.2s ease';
      updateChrome();
    }

    mainImg.style.transition = 'transform 0.2s ease';

    toggle?.addEventListener('click', (e) => {
      e.stopPropagation();
      toggleZoom();
    });

    inner.addEventListener('click', (e) => {
      if (suppressNextInnerClick) {
        e.preventDefault();
        e.stopPropagation();
        suppressNextInnerClick = false;
        return;
      }
      toggleZoom();
    });

    layer.addEventListener('mousemove', (e) => {
      if (!isZoomed || e.target.closest('.radical-pdp__zoom-toggle')) return;
      setPanFromClient(e.clientX, e.clientY);
    });

    layer.addEventListener('mouseenter', (e) => {
      if (!isZoomed || e.target.closest('.radical-pdp__zoom-toggle')) return;
      setPanFromClient(e.clientX, e.clientY);
    });

    layer.addEventListener(
      'touchstart',
      (e) => {
        if (!isZoomed || e.target.closest('.radical-pdp__zoom-toggle')) return;
        if (e.touches.length !== 1) {
          touchPanActive = false;
          return;
        }
        touchPanActive = true;
        touchDidMove = false;
        lastTouchX = e.touches[0].clientX;
        lastTouchY = e.touches[0].clientY;
      },
      { passive: true }
    );

    layer.addEventListener(
      'touchmove',
      (e) => {
        if (!isZoomed || !touchPanActive) return;
        if (e.touches.length !== 1) {
          touchPanActive = false;
          return;
        }
        if (e.target.closest('.radical-pdp__zoom-toggle')) return;
        e.preventDefault();
        const t = e.touches[0];
        const dx = t.clientX - lastTouchX;
        const dy = t.clientY - lastTouchY;
        lastTouchX = t.clientX;
        lastTouchY = t.clientY;
        if (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5) touchDidMove = true;
        panX += dx;
        panY += dy;
        clampPan();
        mainImg.style.transition = 'none';
        applyTransform();
      },
      { passive: false }
    );

    layer.addEventListener(
      'touchcancel',
      () => {
        touchPanActive = false;
      },
      { passive: true }
    );

    layer.addEventListener(
      'touchend',
      (e) => {
        if (e.touches.length > 0) return;
        if (touchPanActive && touchDidMove) suppressNextInnerClick = true;
        touchPanActive = false;
        touchDidMove = false;
      },
      { passive: true }
    );

    return { resetZoom };
  }

  /**
   * @param {string | undefined} src
   * @param {string | undefined} alt
   */
  function setMainImageSrc(mainImg, src, alt) {
    if (!mainImg || !src) return;
    mainImg.src = src;
    if (alt) mainImg.alt = alt;
  }

  /**
   * @param {Record<string, unknown> | null | undefined} resource
   * @returns {number | null}
   */
  function parseVariantPriceCents(resource) {
    const p = resource && resource.price;
    if (typeof p === 'number' && Number.isFinite(p)) return p;
    if (typeof p === 'string') {
      const n = parseFloat(p);
      if (!Number.isFinite(n)) return null;
      return p.includes('.') ? Math.round(n * 100) : Math.round(n);
    }
    return null;
  }

  /**
   * @param {number} cents
   * @param {string} currency
   * @param {boolean} withCode
   */
  function formatMoney(cents, currency, withCode) {
    const amount = cents / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD',
        currencyDisplay: withCode ? 'code' : 'symbol',
      }).format(amount);
    } catch {
      return (Math.round(amount * 100) / 100).toFixed(2);
    }
  }

  /**
   * Gang sheet PDP: fixed unit price from Liquid — only react to quantity changes (no variant:update on section).
   *
   * @param {HTMLElement} root
   */
  function bindGangMinimalLineTotal(root) {
    const formComp = root.querySelector('product-form-component');
    const totalEl = root.querySelector('[data-radical-pdp-line-total]');
    const initRaw = totalEl?.dataset.initialUnitCents;
    let unitCents =
      initRaw != null && initRaw !== '' ? parseInt(String(initRaw), 10) : null;
    if (unitCents !== null && !Number.isFinite(unitCents)) unitCents = null;

    const currency = root.dataset.currency || 'USD';
    const withCode = root.dataset.showCurrencyCode === 'true';

    function getQty() {
      const input = formComp?.querySelector('input[name="quantity"]');
      const n = parseInt(String(input?.value || '1'), 10);
      return Number.isFinite(n) && n >= 1 ? n : 1;
    }

    function updateLineTotal() {
      if (!totalEl || unitCents === null || !Number.isFinite(unitCents)) return;
      totalEl.textContent = formatMoney(unitCents * getQty(), currency, withCode);
    }

    document.addEventListener(
      QUANTITY_SELECTOR_UPDATE,
      /** @param {Event} e */
      (e) => {
        if (!(e.target instanceof Node) || !formComp?.contains(e.target)) return;
        updateLineTotal();
      },
      { passive: true }
    );

    updateLineTotal();
  }

  /**
   * Line total = selected variant unit price (from variant:update JSON) × quantity input.
   * Same events Horizon uses for product-price / price-per-item — does not touch variant fetch or add-to-cart queue.
   *
   * @param {HTMLElement} root
   * @param {HTMLImageElement | null} mainImg
   */
  function bindSectionProductEvents(root, mainImg) {
    const section = root.closest('.shopify-section');
    if (!section) return;

    const formComp = root.querySelector('product-form-component');
    const myProductId = formComp?.dataset.productId;
    const totalEl = root.querySelector('[data-radical-pdp-line-total]');
    const initRaw = totalEl?.dataset.initialUnitCents;
    let unitCents =
      initRaw != null && initRaw !== '' ? parseInt(String(initRaw), 10) : null;
    if (unitCents !== null && !Number.isFinite(unitCents)) unitCents = null;

    const currency = root.dataset.currency || 'USD';
    const withCode = root.dataset.showCurrencyCode === 'true';

    function getQty() {
      const input = formComp?.querySelector('input[name="quantity"]');
      const n = parseInt(String(input?.value || '1'), 10);
      return Number.isFinite(n) && n >= 1 ? n : 1;
    }

    function updateLineTotal() {
      if (!totalEl || unitCents === null || !Number.isFinite(unitCents)) return;
      totalEl.textContent = formatMoney(unitCents * getQty(), currency, withCode);
    }

    section.addEventListener(
      VARIANT_UPDATE,
      /** @param {CustomEvent} e */
      (e) => {
        const detail = e.detail;
        const eventProductId = detail?.data?.productId;
        if (eventProductId != null && myProductId != null && String(eventProductId) !== String(myProductId)) {
          return;
        }

        const resource = detail?.resource;
        const cents = parseVariantPriceCents(resource);
        if (cents != null) unitCents = cents;
        updateLineTotal();

        if (!mainImg) return;
        const preview = resource?.featured_media?.preview_image;
        if (!preview) return;
        const w = preview.width || 1600;
        const base = preview.src;
        if (!base) return;
        try {
          const u = new URL(base, window.location.origin);
          u.searchParams.set('width', String(Math.min(w, 1600)));
          setMainImageSrc(mainImg, u.toString(), preview.alt || mainImg.alt);
        } catch {
          setMainImageSrc(mainImg, base, preview.alt || mainImg.alt);
        }
      }
    );

    document.addEventListener(
      QUANTITY_SELECTOR_UPDATE,
      /** @param {Event} e */
      (e) => {
        if (!(e.target instanceof Node) || !formComp?.contains(e.target)) return;
        updateLineTotal();
      },
      { passive: true }
    );

    updateLineTotal();
  }

  function initRoot(root) {
    const mainImg = /** @type {HTMLImageElement | null} */ (root.querySelector('[data-radical-pdp-main-img]'));
    let zoomApi;

    root.querySelectorAll('.radical-pdp__thumb').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        zoomApi?.resetZoom();
        const src = thumb.getAttribute('data-full-src');
        if (mainImg && src && thumb.getAttribute('data-is-video') !== 'true') {
          mainImg.src = src;
        }
        root.querySelectorAll('.radical-pdp__thumb').forEach((t) => {
          t.classList.toggle('radical-pdp__thumb--active', t === thumb);
          t.setAttribute('aria-selected', t === thumb ? 'true' : 'false');
        });
      });
    });

    if (mainImg) {
      zoomApi = initZoom(root, mainImg);
    }

    if (root.dataset.rdpGangMinimalAtc === 'true') {
      bindGangMinimalLineTotal(root);
    } else {
      bindSectionProductEvents(root, mainImg);
    }
    applySmartBackLink(root);
    initFreshPressTabs(root);
  }

  function run() {
    document.querySelectorAll(PDP_SEL).forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  /**
   * BFCache (back/forward or some product-to-product navigations): custom elements keep DOM but may not
   * re-run connectedCallback, leaving product-form / add-to-cart in a broken state — matches “no gap + ATC dead”.
   * Re-insert product-form-component to force disconnect + reconnect without changing layout order.
   */
  window.addEventListener(
    'pageshow',
    /** @param {PageTransitionEvent} e */
    (e) => {
      if (!e.persisted) return;
      document.querySelectorAll('[data-radical-pdp] product-form-component').forEach((el) => {
        if (!(el instanceof HTMLElement)) return;
        const parent = el.parentNode;
        if (!parent) return;
        const ref = el.nextSibling;
        parent.removeChild(el);
        parent.insertBefore(el, ref);
      });
    },
    { passive: true }
  );
})();
